import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { PlaylistTrack, Track } from '@app/core/models/Track';
import { PlayAudio, Resume, Stop } from '@app/core/states/audio.actions';
import { RadioState } from '@app/core/states/radio.state';
import { ColumnShown } from '@app/library/models/column-shown.model';
import { MovePlaylistTracksRequest } from '@app/library/states/playlist-tracks.actions';
import { AddToPlaylistRequest } from '@app/library/states/playlists.actions';
import { ToggleSelectPlayTrack } from '@app/library/states/selected-tracks.actions';
import { SelectedTracksState } from '@app/library/states/selected-tracks.state';
import { DndDropEvent } from '@app/shared/modules/dnd/directives/dnd-dropzone.directive';
import { platformCmdOrCtrlKey } from '@app/shared/utils';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Navigate } from '@ngxs/router-plugin';
import { Select, Store } from '@ngxs/store';
import { ContextMenuComponent, ContextMenuService } from '@perfectmemory/ngx-contextmenu';
import { BehaviorSubject, fromEvent, Observable } from 'rxjs';
import { filter, map, throttleTime } from 'rxjs/operators';

@Component({
  selector: 'rk-playlist-audio-line',
  templateUrl: './playlist-audio-line.component.html',
  styleUrls: ['./playlist-audio-line.component.scss'],
})
export class PlaylistAudioLineComponent implements OnInit, AfterViewInit {
  @Input()
  set isContextMenuOpen(isOpen: boolean) {
    if (this.hasContextMenuOpen && !isOpen) {
      this.hasContextMenuOpen = false;
    }
  }

  @Input()
  set track(inTrack: PlaylistTrack) {
    this._track = inTrack;
    this.checked$ = this.store
      .select(SelectedTracksState.isPlayTrackSelected)
      .pipe(map(fn => fn(inTrack)));
  }

  get track(): PlaylistTrack {
    return this._track;
  }

  _track: PlaylistTrack;

  @Input()
  tagDisplayedLimiter: number;

  @Input()
  isManualPlaylist: boolean;

  @Input()
  hasPaddingRight = false;

  @Input()
  columns: ColumnShown;

  @Input()
  playlistId: number;

  @Input()
  canSort = true;

  @Input()
  isMobile = false;

  @Input()
  last = false;

  @Input()
  new = false;

  @ViewChild('line', { static: true })
  line: ElementRef<HTMLDivElement>;

  @ViewChild('tagRef') tagZone: ElementRef;

  @ViewChild('playlistRef') playlistZone: ElementRef;

  @Output()
  readonly editTrack = new EventEmitter();

  @Select(SelectedTracksState.numberPlayTrackSelected)
  numberCheckedTracks$: Observable<number>;

  @Output()
  readonly shiftClicked = new EventEmitter<number>();

  checked$: Observable<boolean>;

  @Input()
  isPlaying = false;

  @Input()
  isLoaded = false;

  // Optional
  @Input() contextMenu: ContextMenuComponent;

  @Input()
  isChronic = false;

  hasContextMenuOpen = false;

  dropBefore = false;
  dragSubject = new BehaviorSubject<DragEvent>(undefined);

  constructor(
    private readonly contextMenuService: ContextMenuService,
    private readonly store: Store,
    private readonly ngZone: NgZone,
  ) {}

  ngOnInit() {
    this.dragSubject
      .asObservable()
      .pipe(
        filter(e => !!e),
        throttleTime(50),
      )
      .subscribe(e => {
        this.moved(e);
      });
  }

  ngAfterViewInit(): void {
    if (this.line && this.line.nativeElement) {
      this.line.nativeElement.scrollTop = 0;
      this.ngZone.runOutsideAngular(() => {
        fromEvent<DragEvent>(this.line.nativeElement, 'dragover')
          .pipe(throttleTime(50))
          .subscribe(e => {
            this.dragSubject.next(e);
          });
      });
    }
  }

  selectLine() {
    this.store.dispatch(new ToggleSelectPlayTrack(this.track, true));
  }

  getType(): string {
    return this.isManualPlaylist ? 'playlist_manual' : 'playlist_auto';
  }

  onSelectClickLine(event: MouseEvent) {
    if (platformCmdOrCtrlKey(event)) {
      this.store.dispatch(new ToggleSelectPlayTrack(this.track));

      return;
    }
    if (event.shiftKey) {
      this.shiftClicked.emit(this.track.id);
      event.stopPropagation();
      event.preventDefault();
    }
  }

  @Dispatch()
  playTrack(track: Track) {
    if (this.isPlaying && this.isLoaded) {
      return new Stop();
    }
    if (this.isLoaded) {
      return new Resume();
    }

    return new PlayAudio(track);
  }

  onContextMenu($event: MouseEvent, item: any): void {
    this.hasContextMenuOpen = true;
    this.contextMenuService.show.next({
      item,
      contextMenu: this.contextMenu,
      event: $event,
    });
    $event.preventDefault();
    $event.stopPropagation();
  }

  @Dispatch()
  moveDown(track: PlaylistTrack) {
    return new MovePlaylistTracksRequest(this.track.idPlaylist, this.track.position + 2, [
      track.position,
    ]);
  }

  @Dispatch()
  moveUp(track: PlaylistTrack) {
    return new MovePlaylistTracksRequest(this.track.idPlaylist, this.track.position - 1, [
      track.position,
    ]);
  }

  dndDragImageOffsetFunction(): { x: number; y: number } {
    return { x: 0, y: 0 };
  }

  dropped(event: DndDropEvent) {
    this.removeAllDragHoverClass();
    if (event.data.idPlaylist) {
      this.droppedMoveTrack(event.data);
    } else {
      this.droppedAddTrack(event.data);
    }
  }

  droppedMoveTrack(track: PlaylistTrack) {
    const selectedTracks = this.store.selectSnapshot(
      SelectedTracksState.selectedPlayTracks,
    );
    const isCurrentSelected = this.store.selectSnapshot(
      SelectedTracksState.isPlayTrackSelected,
    )(track);
    const realPosition = this.track.position + (this.dropBefore ? 0 : 1);
    if (isCurrentSelected && selectedTracks.length > 1) {
      this.store.dispatch(
        new MovePlaylistTracksRequest(
          this.track.idPlaylist,
          realPosition,
          selectedTracks.map(val => val.position),
        ),
      );
    } else {
      if (track.position === this.track.position) {
        return;
      }
      this.store.dispatch(
        new MovePlaylistTracksRequest(this.track.idPlaylist, realPosition, [
          track.position,
        ]),
      );
    }
  }

  droppedAddTrack(track: Track) {
    const realPosition = this.track.position + (this.dropBefore ? 0 : 1);
    this.store.dispatch(
      new AddToPlaylistRequest(this.track.idPlaylist, [track.id], realPosition),
    );
  }

  moved(event: any) {
    this.ngZone.run(() => {
      const line = event.target.closest('rk-playlist-audio-line').firstElementChild;
      this.dropBefore = event.clientY - line.getBoundingClientRect().top < 23;
      this.removeAllDragHoverClass(line);
    });
  }

  removeAllDragHoverClass(exception: Element = null) {
    const allLines = document.querySelectorAll(
      'rk-playlist-audio-line .main.dnd-dragover',
    );
    allLines.forEach(line => {
      if (!exception || !exception.isSameNode(line)) {
        line.classList.remove('dnd-dragover');
      }
    });
  }

  @Dispatch()
  openTag(id: number) {
    const radioId = this.store.selectSnapshot(RadioState.currentRadioId);

    return new Navigate(['radio', radioId, 'media', 'tag', id], {}, { replaceUrl: true });
  }

  @Dispatch()
  openPlaylist(id: number) {
    const radioId = this.store.selectSnapshot(RadioState.currentRadioId);

    return new Navigate(
      ['radio', radioId, 'media', 'playlist', id],
      {},
      { replaceUrl: true },
    );
  }
}
